import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { Button, Typography } from '@material-ui/core'
import {
    ProjectBulkAddResult,
    StartProjectBulkAddInput,
} from 'api/graphQL/types/projectBulk.types'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog'
import { ProjectPageContext } from 'pages/ProjectsPage/ProjectPage/components/ProjectPageContext'
import ProjectBulkAddProgressDialog from 'pages/ProjectsPage/WorkspacePage/components/ProjectBulkAddProgressDialog'
import { WorkspaceActionBarContext } from 'pages/ProjectsPage/WorkspacePage/components/WorkspaceActionBarContext'
import React from 'react'
import { useBetaWorkspaceFlag } from 'shared/useBetaWorkspaceFlag'
import { useBulkAddBarAction, useBulkAddBarStore } from './BulkAddBarStore'
import { useBulkAddBarStyles } from './bulkAddBarStyles'

export interface BulkAddBarStyleProps {
    isBetaWorkspaceToggleEnabled?: boolean
}
export interface BulkAddBarProps extends BulkAddBarStyleProps {}

const BulkAddBar = ({}: BulkAddBarProps) => {
    const { currentState } = React.useContext(WorkspaceActionBarContext)
    const {
        selectedBulkAddElements,
        projectId,
        setSelectedBulkAddElements,
    } = React.useContext(ProjectPageContext)
    // hook to check if the beta workspace is enabled using url params
    const { isBetaWorkspaceEnabled } = useBetaWorkspaceFlag()
    const isBetaWorkspaceToggleEnabled =
        (processEnv?.REACT_APP_ShowRedesignedEditorToggle === '1' ||
            isBetaWorkspaceEnabled) &&
        currentState?.showHideStates?.betaWorkspace
    const bulkAddInput: StartProjectBulkAddInput = React.useMemo(
        () => ({
            elements: selectedBulkAddElements ?? [],
            projectId,
        }),
        [selectedBulkAddElements, projectId]
    )
    const classes = useBulkAddBarStyles({ isBetaWorkspaceToggleEnabled })
    const { addBulkItemsInProgress, cancelBulkItems } = useBulkAddBarStore()
    const {
        setCancelBulkItems,
        setBulkAddResponse,
        setBulkAddErrorMessage,
        setAddBulkItemsInProgress,
    } = useBulkAddBarAction()
    const clearBulkAddSelection = React.useCallback(
        (isBulkClear: boolean) => () => {
            if (isBulkClear) {
                setSelectedBulkAddElements([])
            }
            setCancelBulkItems(false)
        },
        [setSelectedBulkAddElements, setCancelBulkItems]
    )
    const handleBulkAddCompleted = React.useCallback(
        (response: Partial<ProjectBulkAddResult>) => {
            setBulkAddResponse(response)
        },
        [setBulkAddResponse]
    )
    const handleBulkAddError = React.useCallback(
        (errorMessage: string | null | undefined) => {
            setBulkAddErrorMessage(errorMessage ?? undefined)
        },
        [setBulkAddErrorMessage]
    )
    const addBulkElementsToProject = React.useCallback(() => {
        // Turn on flag which will show the dialog, which will in turn trigger the mutation
        setAddBulkItemsInProgress(true)
    }, [setAddBulkItemsInProgress])
    return (
        <GridContainer
            data-testid="bulkAddBar"
            className={classes.bulkAddBarContainer}
        >
            <GridItem>
                <Typography className={classes.confirmationText}>
                    {selectedBulkAddElements?.length} element
                    {selectedBulkAddElements &&
                    selectedBulkAddElements?.length > 1
                        ? 's '
                        : ' '}
                    selected
                </Typography>
                <Typography
                    onClick={() => setCancelBulkItems(true)}
                    className={classes.clearText}
                    data-testid={`clearBulkAddBtnId`}
                >
                    Clear
                </Typography>
            </GridItem>
            <GridItem className={classes.elementTypeItem}>
                <Button
                    disableRipple
                    className={clsx(
                        classes.elementTypeButton,
                        isBetaWorkspaceToggleEnabled
                            ? classes.selectedElementsButton
                            : ''
                    )}
                    onClick={addBulkElementsToProject}
                    data-testid={`addSelectedItemsButton`}
                >
                    Add Selected Elements to Project
                </Button>
            </GridItem>
            {addBulkItemsInProgress && (
                <ProjectBulkAddProgressDialog
                    selectedElementCount={selectedBulkAddElements?.length ?? 0}
                    input={bulkAddInput}
                    onCompleted={handleBulkAddCompleted}
                    onError={handleBulkAddError}
                />
            )}
            <ConfirmationDialog
                dialogHeading="Quit Quick Add?"
                openDialog={cancelBulkItems}
                removeHeaderBorderForBulkAdd
                handleDialogClose={clearBulkAddSelection(false)}
                dialogActionCallback={clearBulkAddSelection(true)}
                contentNode={
                    <>
                        Continuing this action will quit the quick add process.
                        The items you have selected for quick adding will not be
                        added to your project.
                        <br />
                        Do you want to continue this action and quit quick
                        adding?
                    </>
                }
                removeFooterBorder={true}
                cancelText="Cancel"
                confirmText="Continue"
                size="small"
                confirmMessage=""
                dialogType="warning"
            />
        </GridContainer>
    )
}

export default BulkAddBar
